import { useState } from "react";
import { toast } from "sonner";
import { Eye } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { ReviewUI } from "@/types/supabase";
import { adminService } from '@/services/adminService';
import { invalidateReviewCaches } from "@/utils/cacheUtils";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import ReviewCard from "./ReviewCard";
import { debugLogger } from '@/utils/debug';

interface ReviewModerationPanelProps {
  reviews: ReviewUI[];
  onReviewUpdated: () => void;
}

const ReviewModerationPanel = ({ reviews, onReviewUpdated }: ReviewModerationPanelProps) => {
  const [reviewToDelete, setReviewToDelete] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const queryClient = useQueryClient();

  const handleVisibilityToggle = async (reviewId: string, isPublic: boolean) => {
    setIsProcessing(true);
    try {
      // Find the review to get provider_id for cache invalidation
      const review = reviews.find(r => r.id === reviewId);
      
  const successRes = await adminService.updateReviewVisibility(reviewId, isPublic);
  const success = successRes.success;
      if (success) {
        // Invalidate relevant caches
        if (review?.providerId) {
          debugLogger.log(`Starting comprehensive cache invalidation for provider ${review.providerId}`);
          
          await invalidateReviewCaches(queryClient, review.providerId, {
            clearLocalStorage: true,
            forceRefetch: true,
            debugMode: true
          });
        }
        
        toast.success(`Review ${isPublic ? 'published' : 'hidden'} successfully`);
        onReviewUpdated();
      } else {
        toast.error("Failed to update review visibility");
      }
    } catch (error) {
      console.error("Error updating review visibility:", error);
      toast.error("An error occurred");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteReview = async (reviewId: string) => {
    setIsProcessing(true);
    try {
      // Find the review to get provider_id for cache invalidation
      const review = reviews.find(r => r.id === reviewId);
      
  const successRes = await adminService.deleteReview(reviewId);
  const success = successRes.success;
      if (success) {
        // Invalidate relevant caches
        if (review?.providerId) {
          debugLogger.log(`Starting comprehensive cache invalidation for provider ${review.providerId} after deletion`);
          
          await invalidateReviewCaches(queryClient, review.providerId, {
            clearLocalStorage: true,
            forceRefetch: true,
            debugMode: true
          });
        }
        
        toast.success("Review deleted successfully");
        onReviewUpdated();
      } else {
        toast.error("Failed to delete review");
      }
    } catch (error) {
      console.error("Error deleting review:", error);
      toast.error("An error occurred");
    } finally {
      setIsProcessing(false);
      setReviewToDelete(null);
    }
  };

  // If there are no reviews, show a message
  if (!reviews || reviews.length === 0) {
    return (
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-xl font-semibold flex items-center">
            <Eye className="h-5 w-5 mr-2 text-gray-600" />
            Admin Review Moderation
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-500 text-center py-4">No reviews to moderate</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {reviews.map((review) => (
        <ReviewCard
          key={review.id}
          review={review}
          onVisibilityToggle={handleVisibilityToggle}
          onDeleteReview={handleDeleteReview}
          isProcessing={isProcessing}
        />
      ))}
    </div>
  );
};

export default ReviewModerationPanel;
